import { LayoutCache, ViewModeLayouts, DashboardLayouts, NamedLayout, ViewModeWidgetData, Widget } from './dashboardLayoutTypes';
import { DEFAULT_CACHE_TIMEOUT } from './dashboardLayoutConstants';
import { getDashboardStorageScope } from '../dashboardStorageScope';

interface OwnerCacheState {
  cache: LayoutCache | null;
  widgetData?: ViewModeWidgetData;
  pendingWidgetData: { [viewMode: string]: Widget[] };
  namedCachedAt: Record<string, number>;
}

/* ============================================================================
 * DASHBOARD LAYOUT CACHE OPERATIONS
 * Manages in-memory cache for dashboard layouts
 * ============================================================================ */

export class DashboardLayoutCache {
  private caches = new Map<string, OwnerCacheState>();
  private cacheTimeout: number;

  constructor(cacheTimeout: number = DEFAULT_CACHE_TIMEOUT) {
    this.cacheTimeout = cacheTimeout;
  }

  resolveOwner(owner?: string): string {
    return owner || getDashboardStorageScope();
  }

  private emptyState(): OwnerCacheState {
    return {
      cache: null,
      widgetData: undefined,
      pendingWidgetData: {},
      namedCachedAt: {},
    };
  }

  private state(owner?: string): OwnerCacheState {
    const resolvedOwner = this.resolveOwner(owner);
    const existing = this.caches.get(resolvedOwner);
    if (existing) {
      return existing;
    }
    const created = this.emptyState();
    this.caches.set(resolvedOwner, created);
    return created;
  }

  /**
   * Check if cache is valid
   */
  isCacheValid(owner?: string): boolean {
    const cache = this.state(owner).cache;
    if (!cache || Object.keys(cache.layouts).length === 0) return false;
    const age = Date.now() - cache.cachedAt;
    return age < this.cacheTimeout;
  }

  /**
   * Get cached layouts
   */
  getCachedLayouts(owner?: string): ViewModeLayouts | null {
    return this.state(owner).cache?.layouts || null;
  }

  getCachedWidgetData(owner?: string): ViewModeWidgetData | undefined {
    return this.state(owner).widgetData;
  }

  /**
   * Get cached named layouts
   */
  getCachedNamedLayouts(viewMode: string, owner?: string): NamedLayout[] | null {
    const state = this.state(owner);
    const at = state.namedCachedAt[viewMode];
    const age = Date.now() - at;
    if (at === undefined || age < 0 || age >= this.cacheTimeout) return null;
    return state.cache?.namedLayouts?.[viewMode] || null;
  }

  /**
   * Get pending changes
   */
  getPendingChanges(owner?: string): { [viewMode: string]: DashboardLayouts } {
    return this.state(owner).cache?.pendingChanges || {};
  }

  getPendingWidgetData(owner?: string): { [viewMode: string]: Widget[] } {
    return this.state(owner).pendingWidgetData;
  }

  /**
   * Check if has pending changes
   */
  hasPendingChanges(owner?: string): boolean {
    const cache = this.state(owner).cache;
    return cache ? Object.keys(cache.pendingChanges).length > 0 : false;
  }

  /**
   * Update cache with new layouts
   */
  updateCache(
    layouts: ViewModeLayouts,
    lastModified: { [viewMode: string]: string },
    widgetData?: ViewModeWidgetData,
    owner?: string
  ): void {
    const state = this.state(owner);
    const pendingChanges = state.cache?.pendingChanges || {};
    state.cache = {
      layouts: { ...layouts, ...pendingChanges },
      namedLayouts: state.cache?.namedLayouts || {},
      lastModified,
      cachedAt: Date.now(),
      pendingChanges,
    };
    if (widgetData) {
      state.widgetData = { ...widgetData, ...state.pendingWidgetData };
    }
  }

  /**
   * Update cached layouts for a specific view mode
   */
  updateViewModeLayout(
    viewMode: string,
    layouts: DashboardLayouts,
    lastModified: string,
    widgetData?: Widget[],
    owner?: string
  ): void {
    const state = this.state(owner);
    if (!state.cache) {
      state.cache = {
        layouts: {},
        namedLayouts: {},
        lastModified: {},
        cachedAt: Date.now(),
        pendingChanges: {},
      };
    }

    state.cache.layouts[viewMode] = layouts;
    state.cache.lastModified[viewMode] = lastModified;
    state.cache.cachedAt = Date.now();

    if (widgetData) {
      state.widgetData = {
        ...(state.widgetData || {}),
        [viewMode]: widgetData,
      };
    }

    if (state.cache.pendingChanges[viewMode]) {
      delete state.cache.pendingChanges[viewMode];
    }
    if (state.pendingWidgetData[viewMode]) {
      delete state.pendingWidgetData[viewMode];
    }
  }

  /**
   * Add pending change
   */
  addPendingChange(viewMode: string, layouts: DashboardLayouts, widgetData?: Widget[], owner?: string): void {
    const state = this.state(owner);
    if (!state.cache) {
      state.cache = {
        layouts: {},
        namedLayouts: {},
        lastModified: {},
        cachedAt: Date.now(),
        pendingChanges: {},
      };
    }

    state.cache.pendingChanges[viewMode] = layouts;
    state.cache.layouts[viewMode] = layouts;
    state.cache.cachedAt = Date.now();

    if (widgetData) {
      state.pendingWidgetData[viewMode] = widgetData;
      state.widgetData = {
        ...(state.widgetData || {}),
        [viewMode]: widgetData,
      };
    }
  }

  /**
   * Remove pending change
   */
  removePendingChange(viewMode: string, owner?: string): void {
    const state = this.state(owner);
    if (state.cache?.pendingChanges[viewMode]) {
      delete state.cache.pendingChanges[viewMode];
    }
    if (state.pendingWidgetData[viewMode]) {
      delete state.pendingWidgetData[viewMode];
    }
  }

  /**
   * Update named layouts cache
   */
  updateNamedLayouts(viewMode: string, namedLayouts: NamedLayout[], owner?: string): void {
    const state = this.state(owner);
    if (!state.cache) {
      state.cache = { layouts: {}, namedLayouts: {}, lastModified: {}, cachedAt: Date.now(), pendingChanges: {} };
    }
    state.cache.namedLayouts[viewMode] = namedLayouts;
    // Only a complete server list establishes freshness. Individual saves or
    // active-layout updates must not extend the age of unrelated named data.
    state.namedCachedAt[viewMode] = Date.now();
  }

  /**
   * Add a named layout to cache
   */
  addNamedLayout(viewMode: string, layout: NamedLayout, setAsDefault = false, owner?: string): void {
    const cache = this.state(owner).cache;
    if (cache) {
      if (!cache.namedLayouts) {
        cache.namedLayouts = {};
      }
      if (!cache.namedLayouts[viewMode]) {
        cache.namedLayouts[viewMode] = [];
      }
      
      // If this is set as default, unmark others
      if (setAsDefault) {
        cache.namedLayouts[viewMode].forEach(l => {
          l.isDefault = false;
        });
      }
      
      cache.namedLayouts[viewMode].push(layout);
    }
  }

  /**
   * Update a named layout in cache
   */
  updateNamedLayout(viewMode: string, updatedLayout: NamedLayout, owner?: string): void {
    const cache = this.state(owner).cache;
    if (!cache) {
      return;
    }

    if (!cache.namedLayouts) {
      cache.namedLayouts = {};
    }

    if (!cache.namedLayouts[viewMode]) {
      cache.namedLayouts[viewMode] = [updatedLayout];
      return;
    }

    if (updatedLayout.isDefault) {
      cache.namedLayouts[viewMode].forEach(layout => {
        layout.isDefault = false;
      });
    }

    const existingIndex = cache.namedLayouts[viewMode].findIndex(layout => layout.id === updatedLayout.id);
    if (existingIndex >= 0) {
      cache.namedLayouts[viewMode][existingIndex] = updatedLayout;
      return;
    }

    cache.namedLayouts[viewMode].push(updatedLayout);
  }

  /**
   * Remove a named layout from cache
   */
  removeNamedLayout(viewMode: string, layoutId: string, owner?: string): void {
    const namedLayouts = this.state(owner).cache?.namedLayouts?.[viewMode];
    if (namedLayouts) {
      this.state(owner).cache!.namedLayouts[viewMode] = namedLayouts.filter(l => l.id !== layoutId);
    }
  }

  /**
   * Update default layout in cache
   */
  setDefaultLayout(viewMode: string, layoutId: string, owner?: string): void {
    const namedLayouts = this.state(owner).cache?.namedLayouts?.[viewMode];
    if (namedLayouts) {
      namedLayouts.forEach(layout => {
        layout.isDefault = layout.id === layoutId;
      });
    }
  }

  /**
   * Clear specific view mode from cache
   */
  clearViewMode(viewMode: string, owner?: string): void {
    const state = this.state(owner);
    if (state.cache) {
      delete state.cache.layouts[viewMode];
      delete state.cache.lastModified[viewMode];
      delete state.cache.pendingChanges[viewMode];
    }
    if (state.widgetData) {
      delete state.widgetData[viewMode];
    }
    if (state.pendingWidgetData[viewMode]) {
      delete state.pendingWidgetData[viewMode];
    }
  }

  /**
   * Clear entire cache
   */
  clearCache(): void {
    this.caches.clear();
  }

  /**
   * Get cache state
   */
  getCacheState(owner?: string): LayoutCache | null {
    return this.state(owner).cache;
  }
}
